import { mapActions } from 'vuex';
import EventBus from '../utils/event-bus';

export default {
  async mounted() {
    await this.$nextTick();

    this.originalHeight = this.$el.offsetHeight;
    this.originalWidth = this.$el.offsetWidth;
    this.resizing = false;
    this.firstX = 0;
    this.firstY = 0;
    this.aspectFactor = 1;
    this.TL = { x: 0, y: 0 };
    this.TR = { x: 0, y: 0 };
    this.BL = { x: 0, y: 0 };
    this.BR = { x: 0, y: 0 };

    const $topRight = document.createElement('div');
    const $bottomRight = document.createElement('div');
    const $topLeft = document.createElement('div');
    const $bottomLeft = document.createElement('div');
    const $topCenter = document.createElement('div');
    const $bottomCenter = document.createElement('div');
    const $leftCenter = document.createElement('div');
    const $rightCenter = document.createElement('div');
    const $rotate = document.createElement('div');

    this.draggablesList = [
      { direction: 'top-right', el: $topRight },
      { direction: 'bottom-right', el: $bottomRight },
      { direction: 'top-left', el: $topLeft },
      { direction: 'bottom-left', el: $bottomLeft },
      { direction: 'top-center', el: $topCenter },
      { direction: 'bottom-center', el: $bottomCenter },
      { direction: 'left-center', el: $leftCenter },
      { direction: 'right-center', el: $rightCenter },
      { direction: 'rotate', el: $rotate },
    ];

    this.draggablesList.forEach((draggable) => {
      this[draggable.direction] = new Draggable(draggable.el, {
        trigger: document.querySelector(`.resize.${draggable.direction}`), // must use .resize class to avoid class conflicts
        type: 'top,left',
        onDragParams: [draggable.direction],
        onDrag: this.update,
        onPress: this.onPress,
        onRelease: this.onRelease,
        dragClickables: false,
      });
    });

    this.bindEvents();
  },

  methods: {
    ...mapActions('graphicsTemplate', [
      'onMoveOrResize',
      'takeHistorySnapshot', // TODO: not sure I need this here now because of new debounce
    ]),
    bindEvents() {
      document.querySelectorAll('.top-right, .top-left, .bottom-left, .bottom-right, .center-right, .center-left, .center-bottom, .center-top')
        .forEach((el) => {
          el.addEventListener('mouseup', this.onMouseUp);
        });
    },

    unbindEvents() {
      document.querySelectorAll('.top-right, .top-left, .bottom-left, .bottom-right, .center-right, .center-left, .center-bottom, .center-top')
        .forEach((el) => {
          el.removeEventListener('mouseup', this.onMouseUp);
        });
    },

    onMouseUp(e) {
      e.stopPropagation();
    },

    update(direction) {
      const { selectedElements } = this.$store.state.templates;
      const isSelected = selectedElements.includes(this.id);

      if (['data-list', 'group'].includes(this.type) && !isSelected) {
        // Don't resize group while user is resizing child
        return;
      }

      if (!isSelected) {
        return;
      }

      const { pointerEvent } = this[direction];
      const aspectLock = pointerEvent.shiftKey || this.aspectLock;

      // The element may have been removed (e.g. a deleted child that is still
      // selected) while a drag callback is in flight.
      if (!this.element?.styles) {
        return;
      }

      const $this = this.$el;
      let {
        // eslint-disable-next-line prefer-const
        top, left, width, height,
      } = this.element.styles;
      const { top: canvasTop, left: canvasLeft } = document.querySelector('.canvas').getBoundingClientRect();

      const rawX = pointerEvent.pageX || this[direction].x;
      const rawy = pointerEvent.pageY || this[direction].y;
      // adjust to be canvas relative since that's the element's coordinate system
      const x = (rawX - canvasLeft) / this.scaleFactor;
      const y = (rawy - canvasTop) / this.scaleFactor;

      if (direction === 'rotate') {
        // Rotate

        if (this.parent !== 'root') {
          const parentEl = this.getElementById(this.parent);
          // The parent may have been removed while a resize/rotate drag is in
          // flight, so guard against a missing element instead of throwing.
          if (parentEl?.styles) {
            left += parentEl.styles.left;
            top += parentEl.styles.top;
          }
        }

        const cx = left + width / 2;
        const cy = top + height / 2;

        let angle = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 90; // convert to degrees
        while (angle < 0) angle += 360;
        angle %= 360;

        this.updateElementProp({
          id: this.element.id,
          propKey: 'rotate',
          propValue: Math.round(angle),
        });
      } else {
        // Resize
        // A lot of this is based on https://github.com/gausszhou/vue3-drag-resize-rotate/blob/master/src/components/vue-drag-resize-rotate/index.vue
        const angle = (this.element.rotate ?? 0) * (Math.PI / 180); // convert to radians

        const cx = left + width / 2;
        const cy = top + height / 2;

        const rotate = (_cx, _cy, _x, _y, _angle) => ({
          x: (_x - _cx) * Math.cos(_angle) - (_y - _cy) * Math.sin(_angle) + _cx,
          y: (_x - _cx) * Math.sin(_angle) + (_y - _cy) * Math.cos(_angle) + _cy,
        });

        if (!this.resizing) {
          this.resizing = true;
          this.TL = rotate(cx, cy, left, top, angle);
          this.TR = rotate(cx, cy, left + width, top, angle);
          this.BL = rotate(cx, cy, left, top + height, angle);
          this.BR = rotate(cx, cy, left + width, top + height, angle);
          this.firstX = x;
          this.firstY = y;
          this.aspectFactor = this.originalWidth / this.originalHeight;
        }

        const deltaX = x - this.firstX;
        const deltaY = y - this.firstY;

        const {
          TL, TR, BL, BR, aspectFactor,
        } = this;
        let diffX;
        let diffY;
        let scale;
        let scaleB;
        let scaleC;
        let newX;
        let newY;
        let newW;
        let newH;
        let Fixed = {}; // fixed point
        let BX = {}; // height edge selected point
        let CX = {}; // width edge selected point
        let Va = {}; // fixed point to mouse vector
        let Vb = {}; // fixed point to projected edge vector
        let Vc = {}; // projection on the other side
        let Vw = {}; // width vector
        let Vh = {}; // height vector

        // drag midpoint
        if (direction.includes('center')) {
          if (aspectLock) return; // don't resize center handles if aspect lock is on

          switch (direction) {
            case 'top-center':
              diffX = deltaX + (TL.x + TR.x) / 2;
              diffY = deltaY + (TL.y + TR.y) / 2;
              Fixed = BL;
              BX = TL;
              CX = BR;
              Va = { x: diffX - Fixed.x, y: diffY - Fixed.y };
              Vb = { x: BX.x - Fixed.x, y: BX.y - Fixed.y };
              scale = (Va.x * Vb.x + Va.y * Vb.y) / (Vb.x ** 2 + Vb.y ** 2);
              Vw = { x: CX.x - Fixed.x, y: CX.y - Fixed.y };
              Vh = { x: Vb.x * scale, y: Vb.y * scale };
              break;
            case 'bottom-center':
              diffX = deltaX + (BL.x + BR.x) / 2;
              diffY = deltaY + (BL.y + BR.y) / 2;
              Fixed = TL;
              BX = BL;
              CX = TR;
              Va = { x: diffX - Fixed.x, y: diffY - Fixed.y };
              Vb = { x: BX.x - Fixed.x, y: BX.y - Fixed.y };
              scale = (Va.x * Vb.x + Va.y * Vb.y) / (Vb.x ** 2 + Vb.y ** 2);
              Vw = { x: CX.x - Fixed.x, y: CX.y - Fixed.y };
              Vh = { x: Vb.x * scale, y: Vb.y * scale };
              break;
            case 'left-center':
              diffX = deltaX + (TL.x + BL.x) / 2;
              diffY = deltaY + (TL.y + BL.y) / 2;
              Fixed = BR;
              BX = BL;
              CX = TR;
              Va = { x: diffX - Fixed.x, y: diffY - Fixed.y };
              Vb = { x: BX.x - Fixed.x, y: BX.y - Fixed.y };
              scale = (Va.x * Vb.x + Va.y * Vb.y) / (Vb.x ** 2 + Vb.y ** 2);
              Vh = { x: CX.x - Fixed.x, y: CX.y - Fixed.y };
              Vw = { x: Vb.x * scale, y: Vb.y * scale };
              break;
            case 'right-center':
              diffX = deltaX + (TR.x + TR.x) / 2;
              diffY = deltaY + (TR.y + TR.y) / 2;
              Fixed = BL;
              BX = BR;
              CX = TL;
              Va = { x: diffX - Fixed.x, y: diffY - Fixed.y };
              Vb = { x: BX.x - Fixed.x, y: BX.y - Fixed.y };
              scale = (Va.x * Vb.x + Va.y * Vb.y) / (Vb.x ** 2 + Vb.y ** 2);
              Vh = { x: CX.x - Fixed.x, y: CX.y - Fixed.y };
              Vw = { x: Vb.x * scale, y: Vb.y * scale };
              break;
            default:
              break;
          }
          // reverse width and height
          newX = Fixed.x + (Vw.x + Vh.x) / 2;
          newY = Fixed.y + (Vw.y + Vh.y) / 2;
          newW = Math.sqrt(Vw.x ** 2 + Vw.y ** 2);
          newH = Math.sqrt(Vh.x ** 2 + Vh.y ** 2);
        } else {
          // drag corner
          switch (direction) {
            case 'top-left':
              diffX = deltaX + TL.x;
              diffY = deltaY + TL.y;
              Fixed = BR;
              BX = BL;
              CX = TR;
              break;
            case 'top-right':
              diffX = deltaX + TR.x;
              diffY = deltaY + TR.y;
              Fixed = BL;
              BX = BR;
              CX = TL;
              break;
            case 'bottom-left':
              diffX = deltaX + BL.x;
              diffY = deltaY + BL.y;
              Fixed = TR;
              BX = TL;
              CX = BR;
              break;
            case 'bottom-right':
              diffX = deltaX + BR.x;
              diffY = deltaY + BR.y;
              Fixed = TL;
              BX = TR;
              CX = BL;
              break;
            default:
              break;
          }
          Va = { x: diffX - Fixed.x, y: diffY - Fixed.y };
          Vb = { x: BX.x - Fixed.x, y: BX.y - Fixed.y };
          Vc = { x: CX.x - Fixed.x, y: CX.y - Fixed.y };
          scaleB = (Va.x * Vb.x + Va.y * Vb.y) / (Vb.x ** 2 + Vb.y ** 2);
          scaleC = (Va.x * Vc.x + Va.y * Vc.y) / (Vc.x ** 2 + Vc.y ** 2);

          if (aspectLock) {
            if (aspectFactor > 1) {
              scaleC = scaleB;
            } else {
              scaleB = scaleC;
            }
          }

          Vw = { x: Vb.x * scaleB, y: Vb.y * scaleB };
          Vh = { x: Vc.x * scaleC, y: Vc.y * scaleC };
          // reverse width and height
          newX = Fixed.x + (Vw.x + Vh.x) / 2;
          newY = Fixed.y + (Vw.y + Vh.y) / 2;
          newW = Math.sqrt(Vw.x ** 2 + Vw.y ** 2);
          newH = Math.sqrt(Vh.x ** 2 + Vh.y ** 2);
        }

        const newLeft = newX - newW / 2;
        const newTop = newY - newH / 2;

        const positionObj = {
          left: Math.round(newLeft),
          top: Math.round(newTop),
          width: Math.round(newW),
          height: Math.round(newH),
        };

        TweenMax.set($this, positionObj);
        this.updateSize();
      }
    },

    onPress(e) {
      e.stopPropagation();

      this.$store.dispatch('setResizing', true);
    },

    updateSize() {
      const el = this.$el;
      const {
        offsetHeight,
        offsetWidth,
        offsetTop,
        offsetLeft,
      } = el;

      this.updateStyles({
        id: this.id,
        propKey: 'width',
        propValue: offsetWidth,
      });
      this.updateStyles({
        id: this.id,
        propKey: 'height',
        propValue: offsetHeight,
      });
      this.updateStyles({
        id: this.id,
        propKey: 'top',
        propValue: offsetTop,
      });
      this.updateStyles({
        id: this.id,
        propKey: 'left',
        propValue: offsetLeft,
      });
      this.originalHeight = offsetHeight;
      this.originalWidth = offsetWidth;

      EventBus.emit('draggable-resized');
    },

    onRelease(e) {
      e.stopPropagation();
      this.resizing = false;

      this.$nextTick(async () => {
        if (this.parent) {
          await this.onMoveOrResize({
            value: {
              groupId: this.parent,
            },
          });
          EventBus.emit('sync-all');
        }
      });

      setTimeout(() => {
        this.$store.dispatch('setResizing', false);
      }, 50);
    },
  },

  beforeUnmount() {
    this.unbindEvents();

    this.draggablesList?.forEach((draggable) => {
      this[draggable.direction].kill();
    });
  },
};
